import { Link } from "react-router-dom";
import { ArrowLeft, Zap, ShieldCheck, CheckCircle2, Activity } from "lucide-react";
import { useEffect, useState, useRef } from "react";
import { useReveal } from "../hooks/useReveal";
import { Logo } from "../components/Logo";

import vueAppImg from "../new-assets/stolen-app-img.webp";

// ─── Fake Emporia Dashboard ───────────────────────────────────────────────────
const CIRCUITS = [
  { name: "HVAC",       base: 1240, color: "#22c55e" },
  { name: "EV Charger", base:  480, color: "#f59e0b" },
  { name: "Kitchen",    base:  380, color: "#3b82f6" },
  { name: "Laundry",    base:  220, color: "#a855f7" },
  { name: "Lighting",   base:   85, color: "#06b6d4" },
];

const BAR_HOURS = [6,7,8,9,10,11,12,13,14,15,16,17,18];
const BASE_BARS = [0.8,1.1,1.4,1.9,2.2,2.0,2.4,2.6,2.3,2.8,3.1,2.7,2.4];

function EmporiaMockup() {
  const [watts, setWatts] = useState(CIRCUITS.map(c => c.base));
  const [total, setTotal] = useState(2.41);
  const [cost, setCost]   = useState(0.31);
  const [bars, setBars]   = useState(BASE_BARS);
  const tick = useRef(0);

  useEffect(() => {
    const id = setInterval(() => {
      tick.current++;
      const newWatts = CIRCUITS.map(c =>
        Math.max(30, c.base + Math.floor((Math.random() - 0.48) * 80))
      );
      const newTotal = +(newWatts.reduce((a,b)=>a+b,0)/1000).toFixed(2);
      setWatts(newWatts);
      setTotal(newTotal);
      setCost(+(newTotal * 0.13).toFixed(2));
      if (tick.current % 4 === 0) {
        setBars(prev => prev.map(v => +Math.max(0.3, v + (Math.random()-0.45)*0.4).toFixed(1)));
      }
    }, 1800);
    return () => clearInterval(id);
  }, []);

  const maxBar = Math.max(...bars);

  return (
    /* Phone shell */
    <div className="relative mx-auto" style={{width:"270px"}}>
      {/* Outer phone frame */}
      <div className="relative rounded-[42px] bg-slate-900 p-[3px] shadow-[0_30px_80px_rgba(0,0,0,0.45)]" style={{boxShadow:"0 0 0 1px #334155, 0 30px 80px rgba(0,0,0,0.5)"}}>
        {/* Side buttons */}
        <div className="absolute left-[-3px] top-20 w-[3px] h-8 bg-slate-600 rounded-l-sm"/>
        <div className="absolute left-[-3px] top-32 w-[3px] h-10 bg-slate-600 rounded-l-sm"/>
        <div className="absolute right-[-3px] top-28 w-[3px] h-14 bg-slate-600 rounded-r-sm"/>

        {/* Screen */}
        <div className="rounded-[40px] overflow-hidden bg-[#0d1117]" style={{height:"560px"}}>

          {/* Dynamic island */}
          <div className="flex justify-center pt-3 pb-1">
            <div className="w-24 h-7 bg-black rounded-full"/>
          </div>

          {/* ── APP CONTENT ── */}
          <div className="px-4 pb-4 flex flex-col h-full overflow-hidden">

            {/* Header */}
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-slate-400 text-[10px] uppercase tracking-widest">Home Energy</p>
                <p className="text-white font-bold text-sm">Live Dashboard</p>
              </div>
              <div className="w-7 h-7 rounded-full bg-green-500/20 flex items-center justify-center">
                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"/>
              </div>
            </div>

            {/* Main usage ring */}
            <div className="flex flex-col items-center mb-4">
              <div className="relative w-32 h-32">
                <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="40" fill="none" stroke="#1e2d1e" strokeWidth="10"/>
                  <circle cx="50" cy="50" r="40" fill="none" stroke="#22c55e" strokeWidth="10"
                    strokeDasharray={`${Math.min(total/5*251,251)} 251`}
                    strokeLinecap="round"
                    style={{transition:"stroke-dasharray 1.2s ease"}}/>
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-white font-black text-2xl leading-none" style={{transition:"all 0.8s"}}>{total}</span>
                  <span className="text-green-400 text-[10px] font-semibold">kW NOW</span>
                </div>
              </div>
              <div className="flex gap-6 mt-2">
                <div className="text-center">
                  <p className="text-slate-400 text-[9px] uppercase">Today</p>
                  <p className="text-white text-xs font-bold">{(total*7.2).toFixed(1)} kWh</p>
                </div>
                <div className="text-center">
                  <p className="text-slate-400 text-[9px] uppercase">Cost/hr</p>
                  <p className="text-green-400 text-xs font-bold">${cost}</p>
                </div>
                <div className="text-center">
                  <p className="text-slate-400 text-[9px] uppercase">Circuits</p>
                  <p className="text-white text-xs font-bold">16</p>
                </div>
              </div>
            </div>

            {/* Mini bar chart */}
            <div className="mb-3">
              <p className="text-slate-400 text-[9px] uppercase tracking-wider mb-1">Today's Usage (kW)</p>
              <div className="flex items-end gap-[3px] h-12">
                {bars.map((v, i) => (
                  <div key={i} className="flex-1 rounded-sm" style={{
                    height: `${(v/maxBar)*100}%`,
                    background: i === bars.length-1 ? "#22c55e" : "#1e3a2a",
                    transition: "height 0.8s ease"
                  }}/>
                ))}
              </div>
              <div className="flex justify-between text-slate-500 text-[8px] mt-1">
                <span>6am</span><span>12pm</span><span>Now</span>
              </div>
            </div>

            {/* Circuit list */}
            <div className="space-y-[6px]">
              <p className="text-slate-400 text-[9px] uppercase tracking-wider">Circuits</p>
              {CIRCUITS.map((c, i) => {
                const circuitWatts = watts[i] ?? 0;

                return (
                  <div key={c.name} className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{background:c.color}}/>
                    <p className="text-slate-300 text-[11px] flex-1">{c.name}</p>
                    <div className="flex-1 h-1 rounded-full bg-slate-800 overflow-hidden">
                      <div className="h-full rounded-full" style={{
                        width:`${Math.min((circuitWatts/2000)*100,100)}%`,
                        background:c.color,
                        transition:"width 1.2s ease"
                      }}/>
                    </div>
                    <p className="text-slate-400 text-[10px] w-10 text-right" style={{transition:"all 0.8s"}}>{circuitWatts}W</p>
                  </div>
                );
              })}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────

export function VueEnergyPage() {
  const headerRef = useReveal<HTMLDivElement>();
  const featuresRef = useReveal<HTMLDivElement>();
  const installRef = useReveal<HTMLDivElement>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-900 font-sans selection:bg-green-600 selection:text-white">

      {/* Clean Nav with both brands */}
      <nav className="w-full bg-white border-b border-slate-200">
        <div className="container mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/">
            <Logo white={false} />
          </Link>
          {/* Emporia product reference in center */}
          <a href="https://www.emporiaenergy.com/" target="_blank" rel="noopener noreferrer" className="hidden md:flex items-center gap-3 bg-slate-900 rounded-full px-5 py-2 hover:opacity-80 transition-opacity">
            <span className="text-slate-400 text-xs font-semibold uppercase tracking-wider">Featuring</span>
            <img src="https://www.emporiaenergy.com/wp-content/uploads/2024/11/logo-white.webp" alt="Emporia Energy" className="h-5 w-auto" />
          </a>
          <Link
            to="/#services"
            className="flex items-center gap-2 text-slate-600 hover:text-green-600 font-medium text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Services
          </Link>
        </div>
      </nav>

      {/* Straightforward Hero */}
      <section className="py-16 lg:py-24 bg-slate-50 border-b border-slate-200">
        <div className="container mx-auto px-6">
          <div ref={headerRef} className="reveal flex flex-col lg:flex-row items-center gap-12 lg:gap-20">

            <div className="lg:w-1/2">
              {/* Product reference row */}
              <div className="flex items-center gap-3 mb-8">
                <Logo white={false} />
                <span className="text-slate-400 text-sm font-medium">installs</span>
                <a href="https://www.emporiaenergy.com/" target="_blank" rel="noopener noreferrer" className="flex items-center bg-[#2a7d2e] rounded-lg px-4 py-2 hover:opacity-90 transition-opacity">
                  <img src="https://www.emporiaenergy.com/wp-content/uploads/2024/11/logo-white.webp" alt="Emporia Energy" className="h-5 w-auto" />
                </a>
              </div>

              <h1 className="text-4xl lg:text-6xl font-black text-slate-900 mb-6 leading-tight tracking-tight">
                Talk To Your <br />
                Energy Data <br />
                <span className="text-green-600">With AI.</span>
              </h1>

              <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-lg">
                Stop guessing why your electric bill is so high. Power One safely integrates the <a href="https://www.emporiaenergy.com/" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline font-semibold">Emporia VUE</a> directly into your main panel so you can track power usage by the second, identify energy hogs, and cut your costs.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="/#contact"
                  className="inline-flex items-center justify-center bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded font-bold text-lg transition-colors"
                >
                  Request Installation Quote
                </a>
              </div>
            </div>

            <div className="lg:w-1/2 flex justify-center lg:justify-end">
              <EmporiaMockup />
            </div>
          </div>
        </div>
      </section>

      {/* Clear Features Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div ref={featuresRef} className="reveal mb-12 text-center max-w-3xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">Why Install Emporia VUE?</h2>
            <p className="text-lg text-slate-600">Get a complete breakdown of where your money goes with an architecture designed to save homeowners an average of 10% monthly.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">

            <div className="bg-slate-50 p-8 rounded-xl border border-slate-200">
              <div className="text-green-600 font-black text-3xl mb-4">01</div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">16-Circuit Granularity</h3>
              <p className="text-slate-600">
                We install individual CT sensors on up to 16 breakers so you know exactly what your AC, dryer, or EV charger is pulling in real-time.
              </p>
            </div>

            <div className="bg-slate-50 p-8 rounded-xl border border-slate-200">
              <div className="text-green-600 font-black text-3xl mb-4">02</div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Real-Time Mobile App</h3>
              <p className="text-slate-600">
                Access second-by-second live usage data directly from your smartphone, free of charge with no monthly subscriptions.
              </p>
            </div>

            <div className="bg-slate-50 p-8 rounded-xl border border-slate-200">
              <div className="text-green-600 font-black text-3xl mb-4">03</div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Appliance Health Alerts</h3>
              <p className="text-slate-600">
                The AI detects unusual power draws and notifies you, acting as an early warning system before expensive appliances break down.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* Animated Ecosystem Section */}
      <section className="py-24 bg-white overflow-hidden border-t border-slate-100">
        <div className="container mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center gap-16 lg:gap-24">

            {/* Animated Left Side */}
            <div className="lg:w-1/2 relative w-full aspect-square max-w-lg mx-auto flex items-center justify-center">
              {/* Spinning dashed ring */}
              <div className="absolute inset-8 rounded-full border-[3px] border-dashed border-green-400/50 animate-[spin_40s_linear_infinite]" />
              <div className="absolute inset-16 rounded-full border border-green-200 animate-[spin_20s_linear_infinite_reverse]" />
              <div className="absolute inset-24 rounded-full border border-green-100" />

              {/* Central pulsing glow */}
              <div className="absolute w-64 h-64 bg-green-400/10 rounded-full blur-3xl animate-pulse" />

              {/* Center App Image */}
              <div className="relative z-10 w-48 shadow-2xl rounded-3xl bg-white rotate-[-5deg] hover:rotate-0 transition-transform duration-500 border border-slate-100 overflow-hidden">
                <img
                  src={vueAppImg}
                  alt="Emporia App"
                  className="w-full h-auto object-contain"
                />
              </div>

              {/* Floating Ecosystem Icons */}
              <div className="absolute inset-0 z-20">
                {/* Top Left - EV Charger */}
                <div className="absolute top-[10%] left-[15%] bg-white p-4 rounded-2xl shadow-xl border border-slate-100 animate-[bounce_3s_ease-in-out_infinite]">
                  <Zap className="w-6 h-6 text-green-600" />
                </div>
                {/* Top Right - Solar */}
                <div className="absolute top-[20%] right-[10%] bg-white p-4 rounded-2xl shadow-xl border border-slate-100 animate-[bounce_4s_ease-in-out_infinite_0.5s]">
                  <Activity className="w-6 h-6 text-green-600" />
                </div>
                {/* Bottom Left - Smart Plugs */}
                <div className="absolute bottom-[20%] left-[10%] bg-white p-4 rounded-2xl shadow-xl border border-slate-100 animate-[bounce_3.5s_ease-in-out_infinite_1s]">
                  <ShieldCheck className="w-6 h-6 text-green-600" />
                </div>
                {/* Bottom Right - Battery */}
                <div className="absolute bottom-[10%] right-[15%] bg-white p-4 rounded-2xl shadow-xl border border-slate-100 animate-[bounce_4.5s_ease-in-out_infinite_1.5s]">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>

            {/* Content Right Side */}
            <div className="lg:w-1/2">
              {/* Emporia brand block */}
              <div className="inline-flex items-center gap-3 bg-[#1e5c22] px-5 py-3 rounded-xl mb-8 group hover:opacity-90 transition-opacity">
                <img
                  src="https://www.emporiaenergy.com/wp-content/uploads/2024/11/logo-white.webp"
                  alt="Emporia Energy"
                  className="h-6 w-auto brightness-110"
                />
                <div className="w-px h-5 bg-green-600" />
                <span className="text-green-300 text-xs font-semibold uppercase tracking-widest">Free App</span>
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6 leading-tight">
                One App. Everything You Need to Know.
              </h2>
              <div className="space-y-6 text-lg text-slate-600 mb-10">
                <p>
                  Open the Emporia app and your home screen shows you what's happening right now — your energy costs, your charger status, and your devices. Today, this week, this month, all without navigating through menus or hunting for the right screen.
                </p>
                <p>
                  The information you check most is right where you need it. And the app is completely free, with no subscriptions and no in-app purchases.
                </p>
              </div>

              {/* App Store Buttons */}
              <div className="flex flex-wrap gap-4">
                <a href="https://www.emporiaenergy.com/app/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 bg-black text-white px-6 py-3 rounded-xl hover:bg-gray-800 transition-colors">
                  <svg className="w-6 h-6 shrink-0" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/>
                  </svg>
                  <div className="text-left">
                    <div className="text-[10px] uppercase tracking-wider font-semibold opacity-80">Download on the</div>
                    <div className="text-lg font-bold leading-none mt-1">App Store</div>
                  </div>
                </a>
                <a href="https://www.emporiaenergy.com/app/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 bg-black text-white px-6 py-3 rounded-xl hover:bg-gray-800 transition-colors">
                  <svg className="w-6 h-6 shrink-0" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22.018 13.298l-3.919 2.218-3.515-3.493 3.543-3.521 3.891 2.202a1.49 1.49 0 0 1 0 2.594zM1.337.924a1.486 1.486 0 0 0-.112.568v21.017c0 .217.045.419.124.6l11.155-11.087L1.337.924zm12.207 10.065l3.258-3.238L3.45.195a1.466 1.466 0 0 0-.946-.179l11.04 10.973zm0 2.067l-11 10.933c.298.036.612-.016.906-.183l13.324-7.54-3.23-3.21z"/>
                  </svg>
                  <div className="text-left">
                    <div className="text-[10px] uppercase tracking-wider font-semibold opacity-80">Get it on</div>
                    <div className="text-lg font-bold leading-none mt-1">Google Play</div>
                  </div>
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Straightforward Installation Details */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="container mx-auto px-6">
          <div ref={installRef} className="reveal max-w-3xl">

            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Professional Installation Required
            </h2>
            <div className="space-y-4 text-slate-300 text-lg mb-8">
              <p>
                Installing the VUE Energy Monitor requires opening your main electrical panel and working directly with live, high-voltage mains and busbars. <strong>It is not a DIY project.</strong>
              </p>
              <p>
                Our licensed electricians safely open your panel, clamp the CT sensors to your highest-draw circuits, handle the Wi-Fi calibration, and ensure the entire system is up to code.
              </p>
            </div>

            <ul className="grid sm:grid-cols-2 gap-4 mb-10">
              {[
                "Safe, code-compliant installation",
                "Custom CT sensor clamping",
                "Wi-Fi setup and calibration",
                "Clean wiring and panel button-up"
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-3 bg-slate-800 px-5 py-4 rounded-lg border border-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-col sm:flex-row gap-4 items-start">
              <div className="flex items-center gap-4 bg-slate-800 p-4 rounded-lg border border-slate-700">
                <ShieldCheck className="w-8 h-8 text-green-500" />
                <div>
                  <div className="font-bold text-white">Hardware Warranty Included</div>
                  <div className="text-sm text-slate-400">1-year manufacturer warranty on your VUE system.</div>
                </div>
              </div>
              <a href="/#contact" className="inline-flex items-center justify-center bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded font-bold text-lg transition-colors whitespace-nowrap">
                Request a Quote
              </a>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
